@if (loadingOverlayService.state().visible) {
  <div class="page-loader" role="status" aria-live="polite" [attr.aria-label]="loadingOverlayService.state().title">
    <div class="page-loader-card">
      <div class="page-loader-mark" aria-hidden="true">
        <span class="page-loader-ring"></span>
        <span class="page-loader-core">PM</span>
      </div>

      <div class="page-loader-copy">
        <strong>{{ loadingOverlayService.state().title }}</strong>
        <span>{{ loadingOverlayService.state().message }}</span>
      </div>
    </div>
  </div>
}

@if (isLoginRoute()) {
  <router-outlet />
  <app-confirmation-modal />
} @else {
  <div class="app-shell" [class.sidebar-collapsed]="sidebarCollapsed()">
    <aside class="sidebar" [class.collapsed]="sidebarCollapsed()" aria-label="Primary navigation">
      <div class="brand">
        <div class="brand-copy">
          <span class="brand-kicker">Home Dept · AP</span>
          <span class="brand-text">Procurement Portal</span>
        </div>
      </div>

      <button
        class="sidebar-toggle"
        type="button"
        [attr.aria-expanded]="!sidebarCollapsed()"
        [attr.aria-label]="sidebarCollapsed() ? 'Expand sidebar menu' : 'Collapse sidebar menu'"
        (click)="toggleSidebar()"
      >
        <i
          class="bi"
          [class.bi-chevron-right]="sidebarCollapsed()"
          [class.bi-chevron-left]="!sidebarCollapsed()"
        ></i>
      </button>

      <nav class="sidebar-nav">
        @for (section of navigationSections(); track section.id) {
          <section class="nav-section" [class.hidden-on-collapse]="sidebarCollapsed()" [attr.aria-labelledby]="'nav-section-' + section.id">
            <div class="nav-heading-row">
              <h2 class="nav-heading" [id]="'nav-section-' + section.id">{{ section.label }}</h2>
              @if (canCollapseSection(section)) {
                <button
                  class="nav-collapse-toggle"
                  type="button"
                  [attr.aria-expanded]="!isSectionCollapsed(section.id)"
                  [attr.aria-controls]="'nav-list-' + section.id"
                  [attr.aria-label]="(isSectionCollapsed(section.id) ? 'Expand ' : 'Collapse ') + section.label"
                  (click)="toggleSection(section.id)"
                >
                  <i
                    class="bi"
                    [class.bi-chevron-down]="isSectionCollapsed(section.id)"
                    [class.bi-chevron-up]="!isSectionCollapsed(section.id)"
                  ></i>
                </button>
              }
            </div>

            <div class="nav-list" [id]="'nav-list-' + section.id" [class.collapsed]="isSectionCollapsed(section.id)">
              @for (item of section.items; track item.path) {
                <a
                  class="nav-link"
                  [routerLink]="item.path"
                  routerLinkActive="active"
                  [routerLinkActiveOptions]="{ exact: item.path === '/dashboard' }"
                  [attr.title]="sidebarCollapsed() ? item.label : null"
                  [attr.aria-label]="item.label"
                >
                  <span class="nav-icon" aria-hidden="true">
                    @switch (item.icon) {
                      @case ('dashboard') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <rect class="solid" x="2" y="2.5" width="4.5" height="4.5" rx="1" />
                          <rect class="solid" x="9.5" y="2.5" width="4.5" height="4.5" rx="1" />
                          <rect class="solid" x="2" y="9.5" width="4.5" height="4.5" rx="1" />
                          <rect class="solid" x="9.5" y="9.5" width="4.5" height="4.5" rx="1" />
                        </svg>
                      }
                      @case ('documents') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <path d="M4 2.5h5l3 3v8H4z" />
                          <path d="M9 2.5v3h3" />
                          <path d="M6 8.5h4" />
                          <path d="M6 11h3" />
                        </svg>
                      }
                      @case ('tenders') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <path d="M8 2.25 13.25 5v6L8 13.75 2.75 11V5z" />
                        </svg>
                      }
                      @case ('ai') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <path d="M8 2.5v2" />
                          <path d="M8 11.5v2" />
                          <path d="M2.5 8h2" />
                          <path d="M11.5 8h2" />
                          <path d="M4.8 4.8 6.2 6.2" />
                          <path d="M9.8 9.8 11.2 11.2" />
                          <path d="M11.2 4.8 9.8 6.2" />
                          <path d="M6.2 9.8 4.8 11.2" />
                          <circle cx="8" cy="8" r="2.25" />
                        </svg>
                      }
                      @case ('templates') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <rect x="3" y="2.75" width="10" height="10.5" rx="1.25" />
                          <path d="M5.25 5.25h5.5" />
                          <path d="M5.25 8h5.5" />
                          <path d="M5.25 10.75h3.25" />
                        </svg>
                      }
                      @case ('manage-sections') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <rect x="2.5" y="3" width="11" height="10" rx="1.25" />
                          <path d="M5 5.5h6" />
                          <path d="M5 8h6" />
                          <path d="M5 10.5h4" />
                          <path d="M3.75 5.5h.01" />
                          <path d="M3.75 8h.01" />
                          <path d="M3.75 10.5h.01" />
                        </svg>
                      }
                      @case ('eligibility') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <path d="M3 8.25 6.25 11.5 13.25 4.5" />
                          <path d="M3.5 3.5h5" />
                          <path d="M3.5 12.5h9" />
                        </svg>
                      }
                      @case ('compliance') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <path d="M8 2.25 12.75 4v3.6c0 3-1.75 5.05-4.75 6.15-3-1.1-4.75-3.15-4.75-6.15V4z" />
                          <path d="m5.75 8 1.5 1.5 3.25-3.25" />
                        </svg>
                      }
                      @case ('evaluation') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <path d="M3 12.75V8.5" />
                          <path d="M8 12.75v-9.5" />
                          <path d="M13 12.75v-6.5" />
                          <path d="M2.5 12.75h11" />
                        </svg>
                      }
                      @case ('submitted-bids') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <path d="M3 3.25h6.5l3.5 3.5v6H3z" />
                          <path d="M9.5 3.25v3.5H13" />
                          <path d="M5.25 8.5h5.5" />
                          <path d="M5.25 10.75h4" />
                        </svg>
                      }
                      @case ('clarifications') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <path d="M3 4.25A1.75 1.75 0 0 1 4.75 2.5h6.5A1.75 1.75 0 0 1 13 4.25v4.5A1.75 1.75 0 0 1 11.25 10.5H8l-3 2.75v-2.75H4.75A1.75 1.75 0 0 1 3 8.75z" />
                          <path d="M5.5 5.5h5" />
                          <path d="M5.5 7.75h3.5" />
                        </svg>
                      }
                      @case ('pre-bid-queries') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <path d="M3 4.25A1.75 1.75 0 0 1 4.75 2.5h6.5A1.75 1.75 0 0 1 13 4.25v4.5A1.75 1.75 0 0 1 11.25 10.5H8l-3.25 3v-3H4.75A1.75 1.75 0 0 1 3 8.75z" />
                          <path d="M5.5 5.5h5" />
                          <path d="M5.5 7.75h3.5" />
                        </svg>
                      }
                      @case ('workflow') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <circle cx="4" cy="4" r="1.75" />
                          <circle cx="12" cy="12" r="1.75" />
                          <path d="M5.75 4h2.5A3.75 3.75 0 0 1 12 7.75v2.5" />
                        </svg>
                      }
                      @case ('users') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <circle cx="5.5" cy="5" r="2" />
                          <circle cx="11.5" cy="5.5" r="1.6" />
                          <path d="M2.2 12.5c.45-2.3 1.55-3.45 3.3-3.45s2.85 1.15 3.3 3.45" />
                          <path d="M8.9 12.25c.35-1.75 1.2-2.65 2.55-2.65 1.3 0 2.15.85 2.45 2.65" />
                        </svg>
                      }
                      @case ('reports') {
                        <svg viewBox="0 0 16 16" focusable="false">
                          <rect x="2.5" y="4" width="11" height="8.5" rx="1.25" />
                          <path d="M4.75 6.25h6.5" />
                        </svg>
                      }
                    }
                  </span>

                  <span class="nav-label">{{ item.label }}</span>

                  @if (item.badge) {
                    <span class="nav-badge">{{ item.badge }}</span>
                  }
                </a>
              }
            </div>
          </section>
        }
      </nav>

      <a class="support-link" href="mailto:support@home-dept.ap.gov.in">
        <span class="nav-icon" aria-hidden="true">
          <svg viewBox="0 0 16 16" focusable="false">
            <circle cx="8" cy="8" r="5.75" />
            <path d="M8 7.5v3" />
            <path d="M8 5.25h.01" />
          </svg>
        </span>
        <span class="support-label">Help &amp; support</span>
      </a>
    </aside>

    <div class="workspace">
      <header class="topbar">
        <label class="search">
          <span>Search</span>
          <input type="search" placeholder="Tender, vendor, document">
        </label>

        <div class="profile">
          <span class="profile-copy">
            <span class="profile-name">{{ profileName() }}</span>
            <span class="profile-role">{{ profileRole() }}</span>
          </span>
          <span class="profile-avatar">{{ profileInitial() }}</span>
          <button class="sign-out" type="button" (click)="logout()">Sign out</button>
        </div>
      </header>

      <main class="content">
        <router-outlet />
      </main>
    </div>
  </div>

  <app-confirmation-modal />
}
